import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { startSTT, stopSTT } from "../sttClient";
import { speakWithAvatar, toggleMute, stopAvatarSpeech, isAvatarSpeaking } from "../ttsPlayer";
import { LANGUAGES } from "../languageConfig";
import { getUserInitials } from "../utils/userUtils";
import { mapLanguage, mapLevel } from "../utils/languageUtils";

// Pronunciation scoring helper
function pronunciationScore(reference, transcript) {
  const refWords = reference.toLowerCase().split(" ");
  const hypWords = transcript.toLowerCase().split(" ");
  let errors = 0;
  for (let i = 0; i < refWords.length; i++) {
    if (refWords[i] !== hypWords[i]) errors++;
  }
  const score = Math.max(
    0,
    100 - (errors / Math.max(refWords.length, 1)) * 100,
  );
  return Math.round(score);
}

// Sanitize bot text to remove markdown artifacts
function sanitizeBotReply(text) {
  if (!text) return text;
  return String(text)
    .replace(/\*\*/g, "")
    .replace(/\*/g, "")
    .replace(/^\s*[-•]\s+/gm, "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/`{1,3}/g, "")
    .trim();
}

const SpeakingSkill = ({
  user,
  language,
  avatar,
  proficiencyLevel,
  avatarVideoRef,
  promptText,
  onConversationStart,
  showPromptsOverlay,
  setShowPromptsOverlay,
  prompts,
  hasStartedConversation,
  isLoadingPrompts,
}) => {
  const [messages, setMessages] = useState([]);
  const [sttActive, setSttActive] = useState(false);
  const [botTyping, setBotTyping] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState("");
  const [partialTranscript, setPartialTranscript] = useState("");
  const [lastBotText, setLastBotText] = useState("");
  const [textMode, setTextMode] = useState(false);
  const [typedText, setTypedText] = useState("");
  const [readingIndex, setReadingIndex] = useState(null);
  const [isReading, setIsReading] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translatingIndex, setTranslatingIndex] = useState(null);
  const [translatedText, setTranslatedText] = useState('');
  const translationRef = useRef(null);

  const isLanguageSelected = Boolean(language);
  const chatEndRef = useRef(null);
  const lastMessageRef = useRef(null);
  const activeRequestId = useRef(0);
  const isInitialMount = useRef(true);
  const textInputRef = useRef(null);
  const promptsOverlayRef = useRef(null);
  const conversationIdRef = useRef(null);
  const userInitials = getUserInitials(user);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';

  // Close prompts overlay when clicking outside
  useEffect(() => {
    if (!showPromptsOverlay) return;
    const handleClickOutside = (e) => {
      if (promptsOverlayRef.current && !promptsOverlayRef.current.contains(e.target)) {
        setShowPromptsOverlay(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPromptsOverlay]);

  // Debug logging
  useEffect(() => {
    console.log("SpeakingSkill mounted with:", {
      language,
      avatar,
      proficiencyLevel,
    });
    // Cleanup STT on unmount
    return () => { stopSTT(); };
  }, []);

  // Handle prompt text from parent component
  useEffect(() => {
    if (promptText) {
      setTypedText(promptText);
      setTextMode(true);
      setTimeout(() => {
        if (textInputRef.current) {
          textInputRef.current.style.height = 'auto';
          textInputRef.current.style.height = Math.min(textInputRef.current.scrollHeight, 120) + 'px';
          textInputRef.current.style.overflowY = textInputRef.current.scrollHeight > 120 ? 'auto' : 'hidden';
        }
      }, 0);
    }
  }, [promptText]);

  // Focus input when text mode is enabled and typedText has content
  useEffect(() => {
    if (textMode && typedText && textInputRef.current) {
      textInputRef.current.focus();
    }
  }, [textMode, typedText]);

  // Helper function to get appropriate voice based on avatar selection
  const getVoiceForAvatar = (avatarValue, language) => {
    const avatarLower = String(avatarValue).toLowerCase();
    const isMale = avatarLower.includes("max") || avatarLower.includes("soren");

    const voiceMap = {
      de: { male: "Hans", female: "Marlene" },
      en: { male: "Matthew", female: "Joanna" },
      fr: { male: "Mathieu", female: "Celine" },
      es: { male: "Enrique", female: "Conchita" },
      it: { male: "Giorgio", female: "Carla" },
      ja: { male: "Takumi", female: "Mizuki" },
    };

    // Normalize language to language code
    const lang = (language || "german").toLowerCase();
    const langCode =
      lang === "german" || lang === "deutsch" || lang === "de"
        ? "de"
        : lang === "english" || lang === "en"
          ? "en"
          : lang === "french" || lang === "français" || lang === "fr"
            ? "fr"
            : lang === "spanish" ||
                lang === "español" ||
                lang === "espanol" ||
                lang === "es"
              ? "es"
              : lang === "italian" || lang === "italiano" || lang === "it"
                ? "it"
                : lang === "japanese" || lang === "日本語" || lang === "ja"
                  ? "ja"
                  : "de";

    console.log(
      "getVoiceForAvatar - language:",
      language,
      "langCode:",
      langCode,
      "isMale:",
      isMale,
    );

    return voiceMap[langCode]?.[isMale ? "male" : "female"] || "Joanna";
  };

  // Map full language names to language codes
  const getLanguageCode = (lang) => {
    const langMap = {
      english: "en",
      french: "fr",
      german: "de",
      spanish: "es",
      italian: "it",
      japanese: "ja",
    };
    return langMap[lang?.toLowerCase()] || lang;
  };

  // Auto-scroll (but not on initial load)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (lastMessageRef.current && messages.length > 0) {
      // Scroll to the top of the latest message so users can read from the beginning
      lastMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [messages, finalTranscript, partialTranscript]);

  // TTS only on "Say It Aloud" click - no auto-invoke

  // Helper to silently save conversation messages to DB
  const saveConversationMessage = async (userText, botReply, evalData) => {
    try {
      const mappedLanguage = mapLanguage(language);
      const mappedLevel = mapLevel(proficiencyLevel);

      if (!conversationIdRef.current) {
        const convRes = await axios.post(`${API_BASE_URL}/chat/conversation`, {
          enterprise_id: user?.email || 'local-user',
          page_name: 'practice-speaking',
          title: userText.substring(0, 100),
          language: mappedLanguage,
          level: mappedLevel
        }, { withCredentials: true });
        if (convRes.data?.conversation_id) {
          conversationIdRef.current = convRes.data.conversation_id;
        }
      }
      if (conversationIdRef.current) {
        // For speaking, include scores in user_input for context
        let userInputWithScores = userText;
        if (evalData) {
          const scores = [];
          if (evalData.pronunciation) {
            scores.push(`Pronunciation - Accuracy: ${evalData.pronunciation.accuracy}%, Fluency: ${evalData.pronunciation.fluency}%, Prosody: ${evalData.pronunciation.prosody}%`);
          }
          if (evalData.grammar?.score !== undefined) {
            scores.push(`Grammar Score: ${evalData.grammar.score}%`);
          }
          if (evalData.grammar?.mistakes?.length > 0) {
            scores.push(`Grammar mistakes: ${evalData.grammar.mistakes.map(m => {
              let s = m.message;
              if (m.suggestions?.length > 0) s += ` → ${m.suggestions[0]}`;
              return s;
            }).join('; ')}`);
          }
          if (scores.length > 0) {
            userInputWithScores += `\n[Evaluation: ${scores.join(' | ')}]`;
          }
        }
        await axios.post(`${API_BASE_URL}/chat/message`, {
          conversation_id: conversationIdRef.current,
          user_input: userInputWithScores,
          agent_response: botReply
        }, { withCredentials: true });
      }
    } catch (saveErr) {
      console.error('Chat save error (non-blocking):', saveErr.message);
    }
  };

  // TEXT MODE handler
  const processUserText = async (userText) => {
    if (!userText || !language) return;

    const langCode = getLanguageCode(language);
    if (!langCode || !LANGUAGES[langCode]) {
      console.error("Language not configured:", language);
      return;
    }

    // Trigger conversation start on first message
    if (onConversationStart && messages.length === 0) {
      onConversationStart();
    }

    // Add user message immediately
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        text: userText,
        pronunciation: null,
        grammarScore: null,
        grammarMistakes: [],
      },
    ]);

    const requestId = ++activeRequestId.current;
    setEvaluating(true);
    setBotTyping(true);
    let localEvalData = null;

    try {
      const evalResp = await fetch("/python/evaluateSpeech", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: userText,
          language: LANGUAGES[langCode].grammar,
        }),
      });
      if (requestId !== activeRequestId.current) return;
      localEvalData = await evalResp.json();
    } catch (e) {
      console.error(e);
    } finally {
      if (requestId === activeRequestId.current) setEvaluating(false);
    }

    // Update the last user message with evaluation data
    if (localEvalData && requestId === activeRequestId.current) {
      setMessages((prev) => {
        const updated = [...prev];
        const lastUserIndex = updated.length - 1;
        if (updated[lastUserIndex]?.role === "user") {
          updated[lastUserIndex] = {
            ...updated[lastUserIndex],
            pronunciation: localEvalData?.pronunciation ?? null,
            grammarScore: localEvalData?.grammar?.score ?? null,
            grammarMistakes: localEvalData?.grammar?.mistakes ?? [],
          };
        }
        return updated;
      });
    }

    try {
      const res = await fetch("/python/getReply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: userText,
          language: LANGUAGES[langCode].label,
        }),
      });
      if (requestId !== activeRequestId.current) return;
      const data = await res.json();
      const cleanReply = sanitizeBotReply(data.reply);
      setMessages((prev) => [...prev, { role: "bot", text: cleanReply }]);
      setLastBotText(cleanReply);
      // Silently save to DB (text mode)
      saveConversationMessage(userText, cleanReply, localEvalData);
    } catch (e) {
      console.error(e);
      if (requestId === activeRequestId.current) {
        setMessages((prev) => [
          ...prev,
          {
            role: "bot",
            text: "There's a problem processing your request. Please try again later, or let me know if I can help.",
          },
        ]);
      }
    } finally {
      if (requestId === activeRequestId.current) setBotTyping(false);
    }
  };

  // STT start
  const handleStart = async () => {
    const langCode = getLanguageCode(language);
    if (!langCode || !LANGUAGES[langCode]) {
      console.error(
        "Language not configured:",
        language,
        "mapped to:",
        langCode,
      );
      return;
    }

    setFinalTranscript("");
    setPartialTranscript("");
    setSttActive(true);

    // Build WebSocket URL with proper protocol
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/stt`;

    startSTT(
      wsUrl,
      { language: LANGUAGES[langCode].stt },
      ({ text, isPartial }) => {
        if (isPartial) {
          setPartialTranscript(text);
        } else {
          setFinalTranscript((prev) => (prev + " " + text).trim());
          setPartialTranscript("");
        }
      },
    );
  };

  // STT stop
  const handleStop = async () => {
    stopSTT();
    setSttActive(false);

    const userText = finalTranscript.trim();
    if (!userText) return;

    const langCode = getLanguageCode(language);
    if (!langCode || !LANGUAGES[langCode]) {
      console.error("Language not configured:", language);
      return;
    }

    // Trigger conversation start on first message
    if (onConversationStart && messages.length === 0) {
      onConversationStart();
    }

    const requestId = ++activeRequestId.current;
    setEvaluating(true);
    setBotTyping(true);

    let evalData = null;
    try {
      const evalResp = await fetch("/python/evaluateSpeech", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: userText,
          language: LANGUAGES[langCode].grammar,
        }),
      });
      if (requestId !== activeRequestId.current) return;
      evalData = await evalResp.json();
    } catch (e) {
      console.error(e);
    } finally {
      if (requestId === activeRequestId.current) setEvaluating(false);
    }

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        text: userText,
        pronunciation: evalData?.pronunciation ?? null,
        grammarScore: evalData?.grammar?.score ?? null,
        grammarMistakes: evalData?.grammar?.mistakes ?? [],
      },
    ]);

    // Clear transcripts AFTER adding to messages
    setFinalTranscript("");
    setPartialTranscript("");

    try {
      const res = await fetch("/python/getReply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: userText,
          language: LANGUAGES[langCode].label,
        }),
      });
      if (requestId !== activeRequestId.current) return;
      const data = await res.json();
      const cleanReply = sanitizeBotReply(data.reply);
      setMessages((prev) => [...prev, { role: "bot", text: cleanReply }]);
      setLastBotText(cleanReply);
      // Silently save to DB (STT mode)
      saveConversationMessage(userText, cleanReply, evalData);
    } catch (e) {
      console.error(e);
      if (requestId === activeRequestId.current) {
        setMessages((prev) => [
          ...prev,
          {
            role: "bot",
            text: "There's a problem processing your request. Please try again later, or let me know if I can help.",
          },
        ]);
      }
    } finally {
      if (requestId === activeRequestId.current) setBotTyping(false);
    }
  };

  // Read bot message aloud using TTS (same as reading/writing skills)
  const handleReadAloud = async (index) => {
    if (isReading && readingIndex === index) {
      stopAvatarSpeech();
      window.speechSynthesis.cancel();
      setIsReading(false);
      setReadingIndex(null);
      return;
    }
    stopAvatarSpeech();
    window.speechSynthesis.cancel();
    const text = messages[index]?.text || '';
    if (!text) return;
    setIsReading(true);
    setReadingIndex(index);
    try {
      const selectedVoice = getVoiceForAvatar(avatar, language);
      await speakWithAvatar(text, null, selectedVoice);
    } catch (e) {
      console.error('TTS error:', e);
    }
  };

  // Listen for TTS completion to reset reading state
  useEffect(() => {
    if (!isReading) return;
    const check = setInterval(() => {
      if (!isAvatarSpeaking()) {
        setIsReading(false);
        setReadingIndex(null);
      }
    }, 500);
    return () => clearInterval(check);
  }, [isReading]);

  // Translate bot message to English
  const handleTranslate = async (index) => {
    setTranslatingIndex(index);
    setShowTranslation(true);
    setTranslatedText('Translating...');
    const text = messages[index]?.text || '';
    const langMap = { 'de': 'German', 'en': 'English', 'fr': 'French', 'es': 'Spanish', 'it': 'Italian', 'ja': 'Japanese' };
    const sourceLang = langMap[language?.toLowerCase()] || 'German';
    try {
      const response = await axios.post(`${API_BASE_URL}/translate`, {
        text, source_language: sourceLang, target_language: 'English', level: proficiencyLevel || 'A1', context: 'speaking practice'
      }, { withCredentials: true });
      setTranslatedText(response.data?.translation || response.data?.translatedText || 'Translation not available');
    } catch (err) {
      console.error('Translation error:', err);
      setTranslatedText('Translation service unavailable.');
    }
  };

  // Close translation panel on outside click
  useEffect(() => {
    if (!showTranslation) return;
    const h = (e) => { if (translationRef.current && !translationRef.current.contains(e.target)) { setShowTranslation(false); setTranslatedText(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showTranslation]);

  return (
    <>
      {/* Messages - Render directly without wrapper */}
      {/* CHAT HISTORY */}
      {messages.map((m, i) =>
        m.role === "user" ? (
          <div
            key={i}
            className="practice-message-row user-message-row"
            style={{justifyContent: 'flex-end', marginTop: '16px'}}
            ref={i === messages.length - 1 ? lastMessageRef : null}
          >
            <div className="practice-message-content-wrapper" style={{maxWidth: '60%'}}>
              <div className="practice-user-message-text" style={{textAlign: 'right'}}>
                {m.text}
              </div>

              {/* Show evaluating indicator if this is the last message and we're evaluating */}
              {i === messages.length - 1 &&
                evaluating &&
                !m.pronunciation && (
                  <div className="practice-evaluating">
                    <div>Evaluating pronunciation...</div>
                    <div>Checking grammar...</div>
                  </div>
                )}

              {/* SCORES ONLY */}
              {m.pronunciation && (
                <div className="practice-scores">
                  <span>Accuracy: {m.pronunciation.accuracy}%</span>
                  <span>Fluency: {m.pronunciation.fluency}%</span>
                  <span>Prosody: {m.pronunciation.prosody}%</span>
                </div>
              )}

              {/* Grammar mistakes */}
              {Array.isArray(m.grammarMistakes) &&
                m.grammarMistakes.length > 0 && (
                  <ul className="practice-grammar-mistakes">
                    {m.grammarMistakes.map((err, idx) => (
                      <li key={idx}>
                        ✗ {err.message}
                        {err.suggestions?.length > 0 && (
                          <>
                            {" "}
                            → <strong>{err.suggestions[0]}</strong>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
            </div>
            <div className="practice-avatar-circle user-avatar" style={{background: '#A100FF', marginLeft: '16px'}}>
              {userInitials}
            </div>
          </div>
        ) : (
          <div
            key={i}
            className="practice-message-row bot-message-row"
            style={{marginTop: '16px', alignItems: 'flex-start'}}
            ref={i === messages.length - 1 ? lastMessageRef : null}
          >
            <div className="practice-avatar-circle bot-avatar">
              <span>LB</span>
            </div>
            <div className="practice-message-content-wrapper" style={{flex: 1}}>
              <div className="practice-bot-message-text">{m.text}</div>
              <div className="practice-message-actions">
                <button className="practice-action-btn" onClick={() => handleReadAloud(i)}>
                  <img src={isReading && readingIndex === i ? "/images/muteicon.svg" : "/images/readicon.svg"} alt="" width="16" height="16" />
                  <span>{isReading && readingIndex === i ? 'Stop' : 'Say It Aloud'}</span>
                </button>
                <button className="practice-action-btn" onClick={() => handleTranslate(i)} style={{marginLeft: '8px'}}>
                  <img src="/images/translateiconactive.svg" alt="" width="16" height="16" />
                  <span>Translate</span>
                </button>
              </div>
            </div>
          </div>
        ),
      )}

      {/* LIVE / CURRENT USER BUBBLE */}
      {(finalTranscript ||
        partialTranscript ||
        (evaluating && (finalTranscript || partialTranscript))) && (
        <div 
          className="practice-message-row user-message-row"
          style={{justifyContent: 'flex-end', marginTop: '16px'}}
        >
          <div className="practice-message-content-wrapper" style={{maxWidth: '60%'}}>
            <div className="practice-user-message-text" style={{textAlign: 'right'}}>
              {[finalTranscript, partialTranscript].filter(Boolean).join(" ")}
              {sttActive && <em> (speaking...)</em>}
            </div>
            {!sttActive && evaluating && (
              <div className="practice-evaluating">
                <div>Evaluating pronunciation...</div>
                <div>Checking grammar...</div>
              </div>
            )}
          </div>
          <div className="practice-avatar-circle user-avatar" style={{background: '#A100FF', marginLeft: '16px'}}>
            {userInitials}
          </div>
        </div>
      )}

      {/* BOT TYPING */}
      {botTyping && (
        <div 
          className="practice-message-row bot-message-row"
          style={{marginTop: '16px', alignItems: 'flex-start'}}
        >
          <div className="practice-avatar-circle bot-avatar">
            <span>LB</span>
          </div>
          <div className="practice-message-content-wrapper" style={{flex: 1}}>
            <div className="practice-typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}

      <div ref={chatEndRef} />

      {/* Speaking Controls at Bottom - Single Line Layout */}
      <div className="practice-input-section practice-speaking-controls">
        <div
          className="practice-speaking-controls-inline"
          style={{ position: "relative" }}
        >
          <label className="practice-type-prompt-checkbox" style={(evaluating || sttActive) ? {opacity: 0.4, cursor: 'not-allowed'} : {}}>
            <input
              type="checkbox"
              checked={textMode}
              onChange={(e) => setTextMode(e.target.checked)}
              disabled={!isLanguageSelected || evaluating || sttActive}
            />
            <span className="practice-checkbox-custom"></span>
            <span className="practice-checkbox-label">
              Type the prompt instead
            </span>
          </label>

          {textMode ? (
            /* Text input mode - send icon inside text box, mic/stop disabled */
            <>
              <div className="practice-text-input-wrapper">
                <textarea
                  ref={textInputRef}
                  value={typedText}
                  disabled={evaluating}
                  onChange={(e) => { setTypedText(e.target.value); setShowPromptsOverlay(false); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; e.target.style.overflowY = e.target.scrollHeight > 120 ? 'auto' : 'hidden'; }}
                  placeholder="Chat with Soren"
                  className="practice-typed-text-input"
                  rows={1}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter" && !e.shiftKey && typedText.trim() && !evaluating) {
                      e.preventDefault();
                      const textToSend = typedText.trim();
                      setTypedText("");
                      e.target.style.height = 'auto';
                      e.target.style.overflowY = 'hidden';
                      await processUserText(textToSend);
                    }
                  }}
                />
                <button
                  className="practice-send-inside"
                  onClick={async () => {
                    if (typedText.trim()) {
                      const textToSend = typedText.trim();
                      setTypedText("");
                      if (textInputRef.current) { textInputRef.current.style.height = 'auto'; textInputRef.current.style.overflowY = 'hidden'; }
                      await processUserText(textToSend);
                    }
                  }}
                  disabled={!typedText.trim() || !isLanguageSelected || evaluating}
                  aria-label="Send"
                  style={{background: typedText.trim() && !evaluating ? '#A100FF' : 'transparent'}}
                >
                  <img alt="Send" width="20" height="20" src={typedText.trim() ? "/images/activeSend.svg" : "/images/sendicon.png"} />
                </button>
              </div>
              <button
                className="practice-mic-button"
                disabled
                aria-label="Mic disabled"
                style={{opacity: 0.3, cursor: 'default'}}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 13C11.66 13 13 11.66 13 10V5C13 3.34 11.66 2 10 2C8.34 2 7 3.34 7 5V10C7 11.66 8.34 13 10 13Z" fill="currentColor" />
                  <path d="M16 8V10C16 13.31 13.31 16 10 16C6.69 16 4 13.31 4 10V8" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </button>
              <button
                className="practice-stop-button"
                disabled
                aria-label="Stop disabled"
                style={{opacity: 0.3, cursor: 'default'}}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="3" y="3" width="10" height="10" fill="currentColor" />
                </svg>
              </button>

              {/* Prompts Icon - Show after conversation starts */}
              {hasStartedConversation && (
                <button
                  className="practice-input-icon-btn"
                  onClick={() => { if (!evaluating) setShowPromptsOverlay(!showPromptsOverlay); }}
                  aria-label="Show prompts"
                  style={{
                    marginLeft: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    ...(evaluating ? { opacity: 0.4, pointerEvents: 'none', cursor: 'not-allowed' } : {}),
                  }}
                >
                  <img
                    src="/images/Group.svg"
                    alt="Prompts"
                    width="12"
                    height="15"
                  />
                  <span style={{ fontSize: "14px" }}>Prompts</span>
                </button>
              )}
            </>
          ) : (
            /* Microphone controls - inline */
            <>
              <button
                className={`practice-mic-button ${sttActive ? "recording" : ""}`}
                onClick={sttActive ? handleStop : handleStart}
                disabled={!isLanguageSelected || evaluating}
                aria-label={sttActive ? "Stop recording" : "Start recording"}
                style={evaluating ? {opacity: 0.4, cursor: 'not-allowed'} : {}}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M10 13C11.66 13 13 11.66 13 10V5C13 3.34 11.66 2 10 2C8.34 2 7 3.34 7 5V10C7 11.66 8.34 13 10 13Z"
                    fill="currentColor"
                  />
                  <path
                    d="M16 8V10C16 13.31 13.31 16 10 16C6.69 16 4 13.31 4 10V8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              </button>

              <button
                className="practice-stop-button"
                onClick={handleStop}
                disabled={!sttActive || !isLanguageSelected}
                aria-label="Stop"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect
                    x="3"
                    y="3"
                    width="10"
                    height="10"
                    fill="currentColor"
                  />
                </svg>
              </button>

              {/* Prompts Icon - Show after conversation starts */}
              {hasStartedConversation && (
                <button
                  className="practice-input-icon-btn"
                  onClick={() => { if (!evaluating) setShowPromptsOverlay(!showPromptsOverlay); }}
                  aria-label="Show prompts"
                  style={{
                    marginLeft: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    ...(evaluating ? { opacity: 0.4, pointerEvents: 'none', cursor: 'not-allowed' } : {}),
                  }}
                >
                  <img
                    src="/images/Group.svg"
                    alt="Prompts"
                    width="12"
                    height="15"
                  />
                  <span style={{ fontSize: "14px" }}>Prompts</span>
                </button>
              )}
            </>
          )}
        </div>

        {/* Prompts Overlay - Same as ReadingPage */}
        {showPromptsOverlay && hasStartedConversation && (
          <div className="practice-prompts-overlay" ref={promptsOverlayRef}>
            <div className="practice-prompts-overlay-header">
              <span>Explore prompt library</span>
            </div>
            <div className="practice-prompts-overlay-content">
              {isLoadingPrompts ? (
                <div style={{ padding: "8px", color: "#707070" }}>
                  Loading prompts...
                </div>
              ) : prompts && prompts.length > 0 ? (
                prompts.map((prompt, idx) => (
                  <button
                    key={idx}
                    className="practice-prompt-overlay-chip"
                    onClick={() => {
                      setTypedText(prompt);
                      setTextMode(true);
                      setShowPromptsOverlay(false);
                      setTimeout(() => {
                        if (textInputRef.current) {
                          textInputRef.current.style.height = 'auto';
                          textInputRef.current.style.height = Math.min(textInputRef.current.scrollHeight, 120) + 'px';
                          textInputRef.current.style.overflowY = textInputRef.current.scrollHeight > 120 ? 'auto' : 'hidden';
                        }
                      }, 0);
                    }}
                  >
                    {prompt}
                  </button>
                ))
              ) : (
                <div style={{ padding: "8px", color: "#707070" }}>
                  No prompts available
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showTranslation && (
        <div className="translation-panel" ref={translationRef}>
          <div className="translation-header">
            <h3>Translation</h3>
            <button className="close-button" onClick={() => { setShowTranslation(false); setTranslatedText(''); }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#000000"/>
              </svg>
            </button>
          </div>
          <div className="translation-content">
            <div className="translation-section">
              <h4>English Translation</h4>
              <div style={{whiteSpace: 'pre-wrap', lineHeight: '1.6'}}>
                {translatedText}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SpeakingSkill;
